import { Apple, Check, Clock, FileText, HandCoins, KeyRound, Landmark, LogOut, Mail, Map, MonitorSmartphone, Phone, Plane, ShieldCheck, UserCheck, UserRound, Wallet, type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { HELP_CONTACTS, HELP_SECTION, mailtoHref, telHref, type HelpContactIcon } from "@aventuria/client-core/help";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../auth/AuthContext";
import { AutofillUsername } from "../components/AutofillUsername";
import { usePageHeader } from "../components/shell/PageHeaderContext";
import { TripMilestones } from "../components/TripMilestones";
import { useLibreWallet, useTrip } from "../lib/queries";
import { useTheme, type ThemePreference } from "../theme/ThemeContext";
import { Spinner } from "../components/Spinner";
import { apiRequest, describeApiError } from "../lib/api";
import { bankNameFromClabe, formatClabe, isValidClabe, sanitizeClabe } from "../lib/clabe";
import { registeredSpeiAccount, type ApiEnvelope, type KuspitDestinationStatus, type LinkedIdentity, type SessionSummary, type SpeiAccount, type User } from "../types";
import { ButtonLabel } from "../components/ButtonLabel";
import { useWelcomeTour } from "../components/WelcomeTour";

function formatDate(value: string) {
  return new Intl.DateTimeFormat("es-MX", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

export function SecurityPage() {
  usePageHeader("Perfil", "Tus datos, tu viaje y la seguridad de tu cuenta.");
  const { user, logout, applyUser } = useAuth();
  const trip = useTrip();
  const { wallet } = useLibreWallet();
  const [sessions, setSessions] = useState<SessionSummary[] | null>(null);
  const [identities, setIdentities] = useState<LinkedIdentity[] | null>(null);
  const [hasPassword, setHasPassword] = useState<boolean>(user?.hasPassword ?? true);
  const [busy, setBusy] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string[] | null>(null);

  const load = useCallback(async () => {
    const [sessionResult, identityResult] = await Promise.all([
      apiRequest<ApiEnvelope<{ sessions: SessionSummary[] }>>("/api/v1/profile/sessions"),
      apiRequest<ApiEnvelope<{ identities: LinkedIdentity[]; hasPassword: boolean }>>("/api/v1/profile/identities")
    ]);
    setSessions(sessionResult.data.sessions);
    setIdentities(identityResult.data.identities);
    setHasPassword(identityResult.data.hasPassword);
  }, []);

  useEffect(() => {
    void load().catch((cause) => setError(describeApiError(cause, "No pudimos cargar tu configuración.")));
  }, [load]);

  const run = async (key: string, action: () => Promise<string>) => {
    setBusy(key); setError(null); setNotice(null);
    try {
      setNotice(await action());
      await load();
    } catch (cause) {
      setError(describeApiError(cause, "No pudimos completar la acción."));
    } finally {
      setBusy(null);
    }
  };

  const otherSessions = (sessions ?? []).filter((session) => !session.current).length;

  return (
    <div className="page-container security-page">
      <section className="security-summary">
        <div className="security-mark"><ShieldCheck size={32} aria-hidden="true" /></div>
        <div><h2>Cuenta protegida</h2><p>Tu sesión está activa en este dispositivo.</p></div>
        <span className="secure-pill"><Check size={15} aria-hidden="true" /> Activa</span>
      </section>

      {notice && <div className="form-message" role="status">{notice}</div>}
      {error?.length && (
        <div className="form-error" role="alert">
          {error.length === 1 ? error[0] : <ul className="form-error-list">{error.map((message) => <li key={message}>{message}</li>)}</ul>}
        </div>
      )}

      {trip.data && <TripMilestones trip={trip.data} saved={wallet?.balance.available ?? 0} />}

      <ProfileSection user={user} onUpdated={applyUser} />

      <HelpSection />

      <AppearanceSection />

      <TourSection />

      <LegalSection />

      <section className="settings-section">
        <div className="section-heading"><div><span className="page-eyebrow">Perfil</span><h2>Datos de acceso</h2></div></div>
        <div className="settings-list">
          <div className="setting-row">
            <span className="setting-icon"><Mail aria-hidden="true" /></span>
            <div><strong>Correo electrónico</strong><span>{user?.email}</span></div>
            {/* Reflects the real state rather than always claiming the address is verified. */}
            <span className={user?.emailVerified ? "verified-text" : "pending-text"}>
              {user?.emailVerified ? <><Check size={15} aria-hidden="true" /> Verificado</> : "Sin verificar"}
            </span>
          </div>
          <div className="setting-row">
            <span className="setting-icon"><UserCheck aria-hidden="true" /></span>
            <div><strong>Identidad</strong><span>Verificación requerida para operar</span></div>
            <span className={user?.kycStatus === "verified" ? "verified-text" : "pending-text"}>
              {user?.kycStatus === "verified" ? <><Check size={15} aria-hidden="true" /> Verificada</> : "Pendiente"}
            </span>
          </div>
        </div>
      </section>

      <SpeiAccountSection user={user} onUpdated={applyUser} />

      <PasswordSection hasPassword={hasPassword} onDone={load} />

      <section className="settings-section">
        <div className="section-heading">
          <div><span className="page-eyebrow">Dispositivos</span><h2>Sesiones activas</h2></div>
          {otherSessions > 0 && (
            <button
              className="secondary-button"
              disabled={busy !== null}
              onClick={() => void run("others", async () => {
                const result = await apiRequest<ApiEnvelope<{ revoked: number }>>("/api/v1/profile/sessions/revoke", {
                  method: "POST", body: JSON.stringify({ scope: "others" })
                });
                return `Cerramos ${result.data.revoked} ${result.data.revoked === 1 ? "sesión" : "sesiones"}.`;
              })}
            >
              {busy === "others" ? <Spinner /> : null} Cerrar las demás
            </button>
          )}
        </div>
        <div className="settings-list">
          {sessions === null && <div className="setting-row"><Spinner label="Cargando sesiones" /></div>}
          {sessions?.map((session) => (
            <div className="setting-row" key={session.id}>
              <span className="setting-icon"><MonitorSmartphone aria-hidden="true" /></span>
              <div>
                <strong>{session.current ? "Este dispositivo" : "Otra sesión"}</strong>
                <span>Inicio {formatDate(session.createdAt)} · expira {formatDate(session.expiresAt)}</span>
              </div>
              {session.current
                ? <span className="verified-text"><Check size={15} aria-hidden="true" /> Actual</span>
                : (
                  <button
                    className="link-button"
                    disabled={busy !== null}
                    onClick={() => void run(session.id, async () => {
                      await apiRequest<ApiEnvelope<{ revoked: number }>>("/api/v1/profile/sessions/revoke", {
                        method: "POST", body: JSON.stringify({ sessionId: session.id })
                      });
                      return "Sesión cerrada.";
                    })}
                  >
                    Cerrar
                  </button>
                )}
            </div>
          ))}
        </div>
      </section>

      {identities !== null && identities.length > 0 && (
        <section className="settings-section">
          <div className="section-heading"><div><span className="page-eyebrow">Acceso</span><h2>Métodos vinculados</h2></div></div>
          <div className="settings-list">
            {identities.map((identity) => (
              <div className="setting-row" key={identity.provider}>
                <span className="setting-icon">
                  {identity.provider === "apple" ? <Apple aria-hidden="true" /> : <UserRound aria-hidden="true" />}
                </span>
                <div>
                  <strong>{identity.provider === "apple" ? "Apple" : "Google"}</strong>
                  <span>{identity.email ?? "Vinculado"} · desde {formatDate(identity.linkedAt)}</span>
                </div>
                <button
                  className="link-button"
                  disabled={busy !== null}
                  // The API refuses to remove the last way in; the copy below explains why.
                  onClick={() => void run(identity.provider, async () => {
                    await apiRequest<ApiEnvelope<{ unlinked: string }>>(
                      `/api/v1/profile/identities/${identity.provider}/unlink`, { method: "POST" }
                    );
                    return "Método desvinculado.";
                  })}
                >
                  Desvincular
                </button>
              </div>
            ))}
          </div>
          {!hasPassword && identities.length === 1 && (
            <p className="field-hint">
              Crea una contraseña antes de desvincular tu único método de acceso.
            </p>
          )}
        </section>
      )}

      <button className="danger-button" onClick={() => void logout()}>
        <LogOut size={18} aria-hidden="true" /> Cerrar sesión
      </button>
    </div>
  );
}

function ProfileSection({ user, onUpdated }: { user: User | null; onUpdated(next: User): void }) {
  const [fullName, setFullName] = useState(user?.fullName ?? "");
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[] | null>(null);
  const phoneClean = phone.replace(/[^0-9+]/g, "");
  const nameDirty = fullName.trim() !== (user?.fullName ?? "") && fullName.trim().length >= 2;
  const phoneDirty = phoneClean !== (user?.phone ?? "");
  const dirty = nameDirty || phoneDirty;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true); setErrors(null); setMessage(null);
    try {
      const result = await apiRequest<ApiEnvelope<{ user: User }>>("/api/v1/profile", {
        method: "PATCH", body: JSON.stringify({ fullName: fullName.trim(), phone: phoneClean || null })
      });
      onUpdated(result.data.user);
      setMessage("Guardamos tus datos.");
    } catch (cause) {
      setErrors(describeApiError(cause, "No pudimos guardar tus datos."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-section">
      <div className="section-heading"><div><span className="page-eyebrow">Perfil</span><h2>Tus datos</h2></div></div>
      <form className="auth-form" onSubmit={submit}>
        <div className="field-block">
          <label htmlFor="profile-name">Nombre completo</label>
          <input
            id="profile-name"
            className="standalone-input"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            autoComplete="name"
            minLength={2}
            maxLength={160}
            required
          />
        </div>
        <div className="field-block">
          <label htmlFor="profile-phone">Teléfono</label>
          <input
            id="profile-phone"
            className="standalone-input"
            inputMode="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            autoComplete="tel"
            placeholder="+52 55 0000 0000"
            maxLength={20}
          />
          <span className="field-hint">Lo usamos para contactarte y, si solicitas un crédito, para que Peso a Peso pueda comunicarse contigo.</span>
        </div>
        {message && <div className="form-message" role="status">{message}</div>}
        {errors?.length && <div className="form-error" role="alert">{errors[0]}</div>}
        <button className="primary-button" type="submit" disabled={saving || !dirty}>
          <ButtonLabel busy={saving} busyLabel="Guardando" label="Guardar" />
        </button>
      </form>
    </section>
  );
}

/**
 * The customer's registered SPEI destination accounts (up to five CLABEs, one default).
 *
 * Identifier fields always start empty â never prefilled from the e-mail or any other profile
 * value â and only input that passes the 18-digit + check-digit validation can be saved. Every
 * mutation (add, remove, change default) asks for the password, because it changes where money
 * leaves to.
 */
function SpeiAccountSection({ user, onUpdated }: { user: User | null; onUpdated(next: User): void }) {
  const [accounts, setAccounts] = useState<SpeiAccount[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [clabe, setClabe] = useState("");
  const [label, setLabel] = useState("");
  const [makeDefault, setMakeDefault] = useState(false);
  // A pending row action (remove / set default / register with Kuspit) waiting for its password confirmation.
  const [pending, setPending] = useState<{ type: "remove" | "default" | "register"; account: SpeiAccount } | null>(null);
  // The cover page (carátula) to send to Kuspit along with the CLABE.
  const [document, setDocument] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[] | null>(null);

  useEffect(() => {
    apiRequest<ApiEnvelope<{ accounts: SpeiAccount[] }>>("/api/v1/profile/spei-accounts")
      .then((response) => setAccounts(response.data.accounts))
      .catch((cause) => setErrors(describeApiError(cause, "No pudimos cargar tus cuentas SPEI.")));
  }, []);

  const applyResponse = (payload: { accounts: SpeiAccount[]; user?: User }) => {
    setAccounts(payload.accounts);
    if (payload.user) onUpdated(payload.user);
  };

  const withStepUp = async (run: (token: string) => Promise<void>) => {
    setSaving(true); setErrors(null); setMessage(null);
    try {
      const stepUp = await apiRequest<ApiEnvelope<{ stepUpToken: string }>>("/api/v1/auth/step-up", {
        method: "POST", body: JSON.stringify({ password })
      });
      await run(stepUp.data.stepUpToken);
      setPassword(""); setPending(null);
    } catch (cause) {
      setErrors(describeApiError(cause, "No pudimos completar la acción."));
    } finally {
      setSaving(false);
    }
  };

  const submitAdd = async (event: FormEvent) => {
    event.preventDefault();
    if (!isValidClabe(clabe)) {
      setErrors(["La CLABE debe tener 18 dígitos y un dígito de control válido."]);
      return;
    }
    await withStepUp(async (token) => {
      const response = await apiRequest<ApiEnvelope<{ accounts: SpeiAccount[]; user?: User }>>("/api/v1/profile/spei-accounts", {
        method: "POST",
        headers: { "x-step-up-token": token },
        body: JSON.stringify({
          clabe,
          ...(label.trim() ? { label: label.trim() } : {}),
          makeDefault: makeDefault || (accounts?.length ?? 0) === 0
        })
      });
      applyResponse(response.data);
      setMessage("Guardamos tu cuenta SPEI.");
      setAdding(false); setClabe(""); setLabel(""); setMakeDefault(false);
    });
  };

  const submitPending = async (event: FormEvent) => {
    event.preventDefault();
    if (!pending) return;
    if (pending.type === "register") {
      if (!document) { setErrors(["Adjunta la carátula de tu cuenta (PDF, PNG o JPEG)."]); return; }
      if (document.size > 8 * 1024 * 1024) { setErrors(["La carátula debe pesar menos de 8 MB."]); return; }
    }
    await withStepUp(async (token) => {
      const response = pending.type === "remove"
        ? await apiRequest<ApiEnvelope<{ accounts: SpeiAccount[]; user?: User }>>(
          `/api/v1/profile/spei-accounts/${pending.account.id}`,
          { method: "DELETE", headers: { "x-step-up-token": token } }
        )
        : pending.type === "default"
          ? await apiRequest<ApiEnvelope<{ accounts: SpeiAccount[]; user?: User }>>(
            `/api/v1/profile/spei-accounts/${pending.account.id}/default`,
            { method: "POST", headers: { "x-step-up-token": token } }
          )
          // The cover page itself is the request body; the API sniffs its type and forwards it to
          // Kuspit with the CLABE (informacionBancaria).
          : await apiRequest<ApiEnvelope<{ accounts: SpeiAccount[]; user?: User }>>(
            `/api/v1/profile/spei-accounts/${pending.account.id}/kuspit-registration?fileName=${encodeURIComponent(document!.name)}`,
            { method: "POST", headers: { "x-step-up-token": token, "content-type": document!.type || "application/octet-stream" }, body: document! }
          );
      applyResponse(response.data);
      setMessage(
        pending.type === "remove" ? "Cuenta eliminada."
          : pending.type === "default" ? "Cuenta predeterminada actualizada."
            : "Enviamos tu cuenta a Kuspit. La autorización suele tardar minutos y como máximo 72 horas; te avisaremos por correo."
      );
      setDocument(null);
    });
  };

  const accountTitle = (account: SpeiAccount) =>
    account.label?.trim() || bankNameFromClabe(account.clabe) || "Cuenta bancaria";

  const registered = accounts ? registeredSpeiAccount(accounts) : null;
  const kuspitChip = (status: KuspitDestinationStatus) => {
    switch (status) {
      case "authorized": return <em className="default-chip">Autorizada para retiros</em>;
      case "pending": return <em className="default-chip">En revisión en Kuspit</em>;
      case "rejected": return <em className="default-chip chip-danger">No autorizada</em>;
      default: return null;
    }
  };

  return (
    <section className="settings-section">
      <div className="section-heading">
        <div><span className="page-eyebrow">Retiros</span><h2>Cuentas SPEI</h2></div>
        {(accounts?.length ?? 0) < 5 && (
          <button
            className="secondary-button"
            onClick={() => { setAdding((value) => !value); setPending(null); setErrors(null); setMessage(null); }}
          >
            {adding ? "Cancelar" : "Agregar cuenta"}
          </button>
        )}
      </div>
      <p className="field-hint">
        Kuspit envía los retiros únicamente a <strong>una</strong> cuenta a tu nombre, registrada en tu contrato y autorizada por su
        equipo. Para registrarla necesitas la carátula de tu cuenta (PDF o imagen). La autorización suele tardar minutos y como
        máximo 72 horas; si registras otra cuenta, reemplaza a la anterior.
      </p>
      <div className="settings-list">
        {accounts === null && <div className="setting-row"><Spinner label="Cargando cuentas SPEI" /></div>}
        {accounts?.length === 0 && (
          <div className="setting-row">
            <span className="setting-icon"><Landmark aria-hidden="true" /></span>
            <div>
              <strong>Sin cuentas registradas</strong>
              <span>Registra tu CLABE de 18 dígitos para retirar por SPEI.</span>
            </div>
          </div>
        )}
        {accounts?.map((account) => (
          <div className="setting-row" key={account.id}>
            <span className="setting-icon"><Landmark aria-hidden="true" /></span>
            <div>
              <strong>
                {accountTitle(account)}
                {kuspitChip(account.kuspit.status)}
                {account.isDefault && account.kuspit.status !== "authorized" && account.kuspit.status !== "pending" && <em className="default-chip">Predeterminada</em>}
              </strong>
              <span>CLABE {formatClabe(account.clabe)} · {bankNameFromClabe(account.clabe) ?? "SPEI"}</span>
              {account.kuspit.status === "pending" && account.kuspit.submittedAt && (
                <span className="field-hint">Enviada a Kuspit el {formatDate(account.kuspit.submittedAt)}. Sabrás que quedó autorizada cuando tu primer retiro se procese.</span>
              )}
              {account.kuspit.status === "rejected" && (
                <span className="field-hint error">Kuspit no autorizó esta cuenta{account.kuspit.message ? `: ${account.kuspit.message}` : "."} Puedes volver a registrarla con una carátula nueva.</span>
              )}
            </div>
            <span className="row-actions">
              {account.kuspit.status !== "pending" && account.kuspit.status !== "authorized" && (
                <button
                  className="link-button"
                  disabled={saving}
                  onClick={() => { setPending({ type: "register", account }); setDocument(null); setAdding(false); setErrors(null); setMessage(null); }}
                >
                  {registered ? "Usar para retiros" : "Registrar para retiros"}
                </button>
              )}
              {!account.isDefault && account.kuspit.status !== "pending" && account.kuspit.status !== "authorized" && (
                <button
                  className="link-button"
                  disabled={saving}
                  onClick={() => { setPending({ type: "default", account }); setAdding(false); setErrors(null); setMessage(null); }}
                >
                  Predeterminar
                </button>
              )}
              {account.kuspit.status !== "pending" && account.kuspit.status !== "authorized" && (
                <button
                  className="link-button danger"
                  disabled={saving}
                  onClick={() => { setPending({ type: "remove", account }); setAdding(false); setErrors(null); setMessage(null); }}
                >
                  Eliminar
                </button>
              )}
            </span>
          </div>
        ))}
      </div>
      {message && <div className="form-message" role="status">{message}</div>}
      {errors?.length && !adding && !pending && (
        <div className="form-error" role="alert">
          {errors.length === 1 ? errors[0] : <ul className="form-error-list">{errors.map((entry) => <li key={entry}>{entry}</li>)}</ul>}
        </div>
      )}

      {pending && (
        <form className="auth-form" onSubmit={submitPending}>
          <AutofillUsername />
          <p className="field-hint">
            {pending.type === "remove"
              ? `Vas a eliminar la cuenta ${accountTitle(pending.account)} (CLABE ${formatClabe(pending.account.clabe)}).`
              : pending.type === "default"
                ? `Los retiros usarán ${accountTitle(pending.account)} (CLABE ${formatClabe(pending.account.clabe)}) como destino predeterminado.`
                : registered
                  ? `Vas a enviar a Kuspit la cuenta ${accountTitle(pending.account)} (CLABE ${formatClabe(pending.account.clabe)}) como tu nuevo destino de retiros. Reemplazará a ${accountTitle(registered)} (CLABE ${formatClabe(registered.clabe)}).`
                  : `Vas a enviar a Kuspit la cuenta ${accountTitle(pending.account)} (CLABE ${formatClabe(pending.account.clabe)}) como destino de tus retiros. La cuenta debe estar a tu nombre.`}
            {" "}Confirma con tu contraseña.
          </p>
          {pending.type === "register" && (
            <div className="field-block">
              <label htmlFor="spei-caratula">Carátula de la cuenta (PDF, PNG o JPEG, máximo 8 MB)</label>
              <input
                id="spei-caratula" className="standalone-input" type="file" accept="application/pdf,image/png,image/jpeg"
                onChange={(event) => setDocument(event.target.files?.[0] ?? null)} required
              />
              <span className="field-hint">
                Es el documento de tu banco que muestra tu nombre y la CLABE (portada del estado de cuenta o constancia de CLABE). Kuspit valida que la cuenta esté a tu nombre.
              </span>
            </div>
          )}
          <div className="field-block">
            <label htmlFor="spei-action-password">Contraseña</label>
            <input
              id="spei-action-password" className="standalone-input" type="password" value={password}
              onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required
            />
          </div>
          {errors?.length && <div className="form-error" role="alert">{errors[0]}</div>}
          <div className="inline-actions">
            <button className={pending.type === "remove" ? "danger-button" : "primary-button"} type="submit" disabled={saving || (pending.type === "register" && !document)}>
              <ButtonLabel
                busy={saving} busyLabel={pending.type === "register" ? "Enviando a Kuspit" : "Procesando"}
                label={pending.type === "remove" ? "Eliminar cuenta" : pending.type === "default" ? "Hacer predeterminada" : "Enviar a Kuspit"}
              />
            </button>
            <button type="button" className="secondary-button" onClick={() => { setPending(null); setErrors(null); }}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {adding && (
        <form className="auth-form" onSubmit={submitAdd}>
          <AutofillUsername />
          <div className="field-block">
            <label htmlFor="spei-clabe-profile">CLABE (18 dígitos)</label>
            <input
              id="spei-clabe-profile"
              className="standalone-input clabe-input"
              inputMode="numeric"
              autoComplete="off"
              data-1p-ignore="true"
              data-lpignore="true"
              data-form-type="other"
              name="spei-clabe-profile"
              placeholder="000 000 00000000000 0"
              value={formatClabe(clabe)}
              onChange={(event) => setClabe(sanitizeClabe(event.target.value))}
              required
            />
            <span className="field-hint">
              {clabe.length === 18
                ? isValidClabe(clabe)
                  ? `${bankNameFromClabe(clabe) ?? "Banco"} · CLABE válida`
                  : "El dígito de control no coincide. Revisa el número."
                : `${clabe.length}/18 dígitos. La encuentras en tu app bancaria o estado de cuenta.`}
            </span>
          </div>
          <div className="field-block">
            <label htmlFor="spei-label-profile">Alias <span className="label-optional">(opcional)</span></label>
            <input
              id="spei-label-profile" className="standalone-input" value={label}
              onChange={(event) => setLabel(event.target.value)} maxLength={40} autoComplete="off"
              placeholder="Mi cuenta de nómina"
            />
          </div>
          {(accounts?.length ?? 0) > 0 && (
            <label className="check-row">
              <input type="checkbox" checked={makeDefault} onChange={(event) => setMakeDefault(event.target.checked)} />
              <span>Usar como cuenta predeterminada para retiros.</span>
            </label>
          )}
          <div className="field-block">
            <label htmlFor="spei-password">Confirma tu contraseña</label>
            <input
              id="spei-password" className="standalone-input" type="password" value={password}
              onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required
            />
          </div>
          {errors?.length && (
            <div className="form-error" role="alert">
              {errors.length === 1 ? errors[0] : <ul className="form-error-list">{errors.map((entry) => <li key={entry}>{entry}</li>)}</ul>}
            </div>
          )}
          <button className="primary-button" type="submit" disabled={saving || !isValidClabe(clabe)}>
            <ButtonLabel busy={saving} busyLabel="Guardando" label="Guardar cuenta SPEI" />
          </button>
        </form>
      )}
    </section>
  );
}

function PasswordSection({ hasPassword, onDone }: { hasPassword: boolean; onDone(): Promise<void> }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[] | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setErrors(null); setMessage(null);
    if (next !== confirmation) { setErrors(["Las contraseñas no coinciden."]); return; }
    setSaving(true);
    try {
      // A first password and a change are separate routes, because only one of them can prove
      // control of an existing credential.
      const result = hasPassword
        ? await apiRequest<ApiEnvelope<{ message: string }>>("/api/v1/profile/password", {
          method: "POST", body: JSON.stringify({ currentPassword: current, newPassword: next })
        })
        : await apiRequest<ApiEnvelope<{ user: User }>>("/api/v1/auth/password/set", {
          method: "POST", body: JSON.stringify({ password: next })
        });
      setMessage("message" in result.data ? result.data.message : "Creamos tu contraseña.");
      setCurrent(""); setNext(""); setConfirmation("");
      await onDone();
    } catch (cause) {
      setErrors(describeApiError(cause, "No pudimos actualizar tu contraseña."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-section">
      <div className="section-heading">
        <div>
          <span className="page-eyebrow">Seguridad</span>
          <h2>{hasPassword ? "Cambiar contraseña" : "Crear contraseña"}</h2>
        </div>
      </div>
      <form className="auth-form" onSubmit={submit}>
        {!hasPassword && (
          <p className="field-hint">
            Tu cuenta se creó con Apple o Google. Las operaciones financieras necesitan una contraseña.
          </p>
        )}
        {hasPassword && (
          <div className="field-block">
            <label htmlFor="current-password">Contraseña actual</label>
            <input
              id="current-password" className="standalone-input" type="password" value={current}
              onChange={(event) => setCurrent(event.target.value)} autoComplete="current-password" required
            />
          </div>
        )}
        <div className="field-block">
          <label htmlFor="new-password">Nueva contraseña</label>
          <input
            id="new-password" className="standalone-input" type="password" value={next}
            onChange={(event) => setNext(event.target.value)} autoComplete="new-password" minLength={12} required
          />
          <span className="field-hint">12 caracteres, mayúscula, minúscula, número y símbolo.</span>
        </div>
        <div className="field-block">
          <label htmlFor="confirm-password">Confirma la contraseña</label>
          <input
            id="confirm-password" className="standalone-input" type="password" value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)} autoComplete="new-password" minLength={12} required
          />
        </div>
        {message && <div className="form-message" role="status">{message}</div>}
        {errors?.length && (
          <div className="form-error" role="alert">
            {errors.length === 1 ? errors[0] : <ul className="form-error-list">{errors.map((entry) => <li key={entry}>{entry}</li>)}</ul>}
          </div>
        )}
        <button className="primary-button" type="submit" disabled={saving}>
          <ButtonLabel busy={saving} busyLabel="Guardando" label={hasPassword ? "Actualizar" : "Crear"} icon={<KeyRound size={16} aria-hidden="true" />} iconFirst />
        </button>
      </form>
    </section>
  );
}

const THEMES: Array<{ id: ThemePreference; label: string; hint: string }> = [
  { id: "light", label: "Claro", hint: "Fondo claro, siempre" },
  { id: "dark", label: "Oscuro", hint: "Fondo oscuro, siempre" },
  { id: "system", label: "Sistema", hint: "Sigue tu dispositivo" }
];

const HELP_ICONS: Record<HelpContactIcon, LucideIcon> = { plane: Plane, wallet: Wallet, "hand-coins": HandCoins };

/** "¿Necesitas ayuda?": who to call for trips, the wallet and loans (Perfil · Ayuda, 13 Oct 2026). */
function HelpSection() {
  return (
    <section className="settings-section" aria-labelledby="help-title">
      <div className="section-heading"><div><span className="page-eyebrow">{HELP_SECTION.eyebrow}</span><h2 id="help-title">{HELP_SECTION.title}</h2><p>{HELP_SECTION.subtitle}</p></div></div>
      <div className="help-contacts">
        {HELP_CONTACTS.map((contact) => {
          const Icon = HELP_ICONS[contact.icon];
          return (
            <article key={contact.id} className="help-contact">
              <header>
                <span className="setting-icon"><Icon aria-hidden="true" /></span>
                <div><strong>{contact.topic}</strong><span>{contact.partner}</span></div>
              </header>
              <ul className="help-contact-lines">
                {contact.email && <li><Mail size={16} aria-hidden="true" /><span translate="no">{contact.email}</span></li>}
                <li><Phone size={16} aria-hidden="true" /><span translate="no">{contact.phone}</span></li>
                <li><Clock size={16} aria-hidden="true" /><span>{contact.hours}</span></li>
              </ul>
              <div className="help-contact-actions">
                <a className="primary-button solid small" href={telHref(contact.phone)} aria-label={`Llamar a ${contact.partner}`}><Phone size={16} aria-hidden="true" /> Llamar</a>
                {contact.email && <a className="secondary-button small" href={mailtoHref(contact.email)} aria-label={`Escribir a ${contact.partner}`}><Mail size={16} aria-hidden="true" /> Escribir</a>}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/** Perfil → Legal: every legal document and the providers' notices on one page (client review, 22 Oct 2026). */
function LegalSection() {
  return (
    <section className="settings-section">
      <div className="section-heading"><div><span className="page-eyebrow">Legal</span><h2>Información legal</h2></div></div>
      <div className="nudge-card">
        <span aria-hidden="true"><FileText size={20} /></span>
        <div>
          <strong>Términos, privacidad y avisos legales</strong>
          <small>Términos y condiciones, aviso de privacidad, política de cookies, condiciones de viaje y los avisos de Kuspit y Peso a Peso.</small>
        </div>
        <Link to="/legal" className="secondary-button small">Ver documentos</Link>
      </div>
    </section>
  );
}

/** Replays the welcome tour ("Pop-up de bienvenida") on request. */
function TourSection() {
  const tour = useWelcomeTour();
  return (
    <section className="settings-section">
      <div className="section-heading"><div><span className="page-eyebrow">Ayuda</span><h2>Recorrido de bienvenida</h2></div></div>
      <div className="nudge-card">
        <span aria-hidden="true"><Map size={20} /></span>
        <div>
          <strong>Conoce la app en 30 segundos</strong>
          <small>Viajes, Wallet Libre, Apartado y Viaja ahora, paga después: el recorrido que viste al entrar por primera vez.</small>
        </div>
        <button type="button" className="secondary-button small" onClick={tour.open}>Ver el recorrido otra vez</button>
      </div>
    </section>
  );
}

function AppearanceSection() {
  const { preference, setPreference } = useTheme();
  return (
    <section className="settings-section">
      <div className="section-heading"><div><span className="page-eyebrow">Apariencia</span><h2>Tema</h2></div></div>
      <div className="theme-options" role="radiogroup" aria-label="Tema de la aplicación">
        {THEMES.map((option) => (
          <button key={option.id} type="button" role="radio" aria-checked={preference === option.id} className={`theme-option${preference === option.id ? " active" : ""}`} onClick={() => setPreference(option.id)}>
            <strong>{option.label}</strong><span>{option.hint}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
